import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Switch,
  Input,
  Select,
  InputNumber,
  message,
  Alert,
  Collapse,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { API, apiErrorMessage } from "../../../../helpers/API/amplifyApi";
import MondayButton from "../../../commonComponents/MondayButton/MondayButton";
import { useSelector } from "react-redux";
import dayjsTZ from "../../../../utils/dayjs";
import GmailSetupWizard from "./GmailSetupWizard";
import { useAuth } from "../../../../authentication/authHelpers";
import { isBranchManager, getManagedTeam } from "../../../../utils/branchScope";
import {
  canAccessGmailIntegration,
  gmailScopeQuery,
  resolveGmailScopeId,
  withGmailScope,
} from "../../../../utils/gmailScope";
import "./GmailInboxSettings.scss";

const FALLBACK_MODELS = [
  "gpt-4o-mini",
  "gpt-4o",
  "gpt-4.1-mini",
  "gpt-4.1",
  "o4-mini",
];

function Section({ title, children, extra }) {
  return (
    <section className="gmail-inbox-settings__section">
      <header className="gmail-inbox-settings__section-head">
        <h2 className="gmail-inbox-settings__section-title">{title}</h2>
        {extra || null}
      </header>
      <div className="gmail-inbox-settings__section-body">{children}</div>
    </section>
  );
}

const GmailInboxSettings = () => {
  const auth = useAuth();
  const allowed = canAccessGmailIntegration(auth);
  const scopeId = resolveGmailScopeId(auth);
  const branchTeam = useMemo(
    () => (isBranchManager(auth) ? getManagedTeam(auth) : null),
    [auth]
  );
  const canEditSecrets = !isBranchManager(auth);
  const { isDarkMode } = useSelector((state) => state.usersList);
  const [cfg, setCfg] = useState(null);
  const [saJson, setSaJson] = useState("");
  const [oauthToken, setOauthToken] = useState("");
  const [openaiKey, setOpenaiKey] = useState("");
  const [modelOptions, setModelOptions] = useState(FALLBACK_MODELS);
  const [modelsSource, setModelsSource] = useState("fallback");
  const [loadingModels, setLoadingModels] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [lastSyncResult, setLastSyncResult] = useState(null);

  const loadModels = useCallback(async () => {
    if (!scopeId) return;
    setLoadingModels(true);
    try {
      const res = await API.get(
        "gmailInbox",
        "/gmailInbox/openai/models",
        gmailScopeQuery(auth)
      );
      const list = Array.isArray(res?.models) ? res.models : FALLBACK_MODELS;
      setModelOptions(list.length ? list : FALLBACK_MODELS);
      setModelsSource(res?.source || "fallback");
      if (res?.selected) {
        setCfg((c) =>
          c && !c.openaiModel ? { ...c, openaiModel: res.selected } : c
        );
      }
    } catch {
      setModelOptions(FALLBACK_MODELS);
      setModelsSource("fallback");
    } finally {
      setLoadingModels(false);
    }
  }, [auth, scopeId]);

  const load = useCallback(async () => {
    if (!scopeId) {
      message.error("Mungon dega e menaxhuar për Gmail");
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await API.get(
        "gmailInbox",
        "/gmailInbox/config",
        gmailScopeQuery(auth)
      );
      setCfg(res);
    } catch {
      message.error("Nuk u lexua konfigurimi i Gmail");
      setCfg({
        enabled: false,
        mode: "serviceAccount",
        delegatedMailbox: "",
        query:
          'subject:(leje OR pushim OR pushime OR leave OR "annual leave" OR "leave request") -from:(duapune.com OR linkedin.com OR noreply OR no-reply OR m.higgsfield.ai)',
        pollMinutes: 15,
        openaiModel: "gpt-4o-mini",
        initialLookbackMonths: 6,
        backfillCompleted: false,
        hasServiceAccountJson: false,
        hasOpenAiKey: false,
        scopeId,
      });
    } finally {
      setLoading(false);
    }
  }, [auth, scopeId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!loading && cfg) loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, cfg?.hasOpenAiKey]);

  const save = async () => {
    setSaving(true);
    try {
      const body = withGmailScope(
        {
          enabled: !!cfg.enabled,
          mode: cfg.mode || "serviceAccount",
          delegatedMailbox: cfg.delegatedMailbox || "",
          query: cfg.query || "",
          pollMinutes: cfg.pollMinutes || 15,
          openaiModel: cfg.openaiModel || "gpt-4o-mini",
          initialLookbackMonths: cfg.initialLookbackMonths || 6,
        },
        auth
      );
      if (canEditSecrets && saJson.trim()) {
        JSON.parse(saJson);
        body.serviceAccountJson = saJson.trim();
      }
      if (canEditSecrets && oauthToken.trim()) {
        body.oauthRefreshToken = oauthToken.trim();
      }
      if (canEditSecrets && openaiKey.trim()) {
        body.openaiApiKey = openaiKey.trim();
      }

      const res = await API.put("gmailInbox", "/gmailInbox/config", {
        body,
        ...gmailScopeQuery(auth),
      });
      setCfg(res);
      setSaJson("");
      setOauthToken("");
      setOpenaiKey("");
      message.success("Konfigurimi u ruajt (sekretet vetëm në SSM)");
      loadModels();
    } catch (e) {
      message.error(
        apiErrorMessage(e, "Ruajtja dështoi — kontrollo JSON / çelësat")
      );
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await API.post("gmailInbox", "/gmailInbox/test", {
        body: withGmailScope({}, auth),
        ...gmailScopeQuery(auth),
      });
      setTestResult(res);
      message.success(`Lidhur me ${res.emailAddress}`);
    } catch (e) {
      message.error(apiErrorMessage(e, "Testi dështoi"));
    } finally {
      setTesting(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await API.post("gmailInbox", "/gmailInbox/sync", {
        body: withGmailScope({}, auth),
        ...gmailScopeQuery(auth),
      });
      setLastSyncResult(res);
      message.success(
        `Sync (${res.phase || "?"}): ${res.fetched ?? 0} email, ${
          res.queued ?? 0
        } të reja, ${res.skippedExisting ?? 0} ekzistuese`
      );
      load();
    } catch (e) {
      message.error(apiErrorMessage(e, "Sync dështoi"));
    } finally {
      setSyncing(false);
    }
  };

  const resetBackfill = async () => {
    setSaving(true);
    try {
      const res = await API.put("gmailInbox", "/gmailInbox/config", {
        body: withGmailScope(
          {
            enabled: !!cfg.enabled,
            mode: cfg.mode,
            delegatedMailbox: cfg.delegatedMailbox,
            query: cfg.query,
            pollMinutes: cfg.pollMinutes,
            openaiModel: cfg.openaiModel,
            initialLookbackMonths: cfg.initialLookbackMonths || 6,
            resetBackfill: true,
          },
          auth
        ),
        ...gmailScopeQuery(auth),
      });
      setCfg(res);
      message.success("Backfill u rivendos — sync tjetër fillon nga lookback");
    } catch (e) {
      message.error(apiErrorMessage(e, "Rivendosja dështoi"));
    } finally {
      setSaving(false);
    }
  };

  const pageClass = `gmail-inbox-settings${isDarkMode ? " dark" : ""}`;

  if (!allowed || !scopeId) {
    return (
      <div className={pageClass} data-testid="gmail-inbox-settings">
        <Alert
          type="error"
          showIcon
          message="Akses i ndaluar"
          description="Integrimi Gmail është i kufizuar. Vetëm llogaritë e autorizuara mund ta hapin këtë faqe."
        />
      </div>
    );
  }

  if (loading || !cfg) {
    return (
      <div className={pageClass} data-testid="gmail-inbox-settings">
        Duke ngarkuar…
      </div>
    );
  }

  const isOn = !!cfg.enabled;
  const modelSelectOptions = Array.from(
    new Set([...(modelOptions || []), cfg.openaiModel].filter(Boolean))
  ).map((id) => ({ value: id, label: id }));

  return (
    <div className={pageClass} data-testid="gmail-inbox-settings">
      <header className="gmail-inbox-settings__header">
        <div className="gmail-inbox-settings__heading">
          <h1 className="gmail-inbox-settings__title">
            Gmail — Inbox pushimesh
            {branchTeam ? ` (${branchTeam})` : ""}
          </h1>
          <p className="gmail-inbox-settings__subtitle">
            Lexon inbox-in e HR, nxjerr kërkesat e pushimit me OpenAI dhe i
            vendos në radhën e Planerit. Sekretet ruhen në AWS SSM.
          </p>
        </div>
        <span
          className="gmail-inbox-settings__badge"
          data-on={isOn ? "true" : "false"}
          role="status"
        >
          {isOn ? "Aktiv" : "Jo aktiv"}
        </span>
      </header>

      {branchTeam ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Inbox i veçantë për degën ${branchTeam}`}
          description="Ky konfigurim dhe radha e Planerit janë të izoluara nga inbox-i i kompanisë. Vendos mailbox-in e degës më poshtë; kredencialet e Google/OpenAI trashëgohen nga konfigurimi i kompanisë."
        />
      ) : null}

      <Section title="Status & sync">
        <div
          className="gmail-inbox-settings__master"
          data-on={isOn ? "true" : "false"}
        >
          <div>
            <p className="gmail-inbox-settings__master-label">
              Sync automatik (cron)
            </p>
            <p className="gmail-inbox-settings__master-hint">
              {isOn
                ? "ON — cron dhe Sync tani lexojnë inbox-in"
                : "OFF — cron nuk ekzekutohet; Sync tani ende funksionon"}
            </p>
          </div>
          <div className="gmail-inbox-settings__master-controls">
            <Switch
              checked={isOn}
              onChange={(enabled) => setCfg({ ...cfg, enabled })}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
          </div>
        </div>

        <div className="gmail-inbox-settings__meta">
          <div className="gmail-inbox-settings__meta-item">
            <span className="gmail-inbox-settings__meta-label">Faza</span>
            <span className="gmail-inbox-settings__meta-value">
              {cfg.backfillCompleted
                ? "Incremental (ditore)"
                : "Backfill (lookback)"}
            </span>
          </div>
          <div className="gmail-inbox-settings__meta-item">
            <span className="gmail-inbox-settings__meta-label">Deri më</span>
            <span className="gmail-inbox-settings__meta-value">
              {cfg.syncedThroughMs
                ? dayjsTZ(cfg.syncedThroughMs).format("DD/MM/YYYY HH:mm")
                : "—"}
            </span>
          </div>
          <div className="gmail-inbox-settings__meta-item">
            <span className="gmail-inbox-settings__meta-label">
              Sync i fundit
            </span>
            <span className="gmail-inbox-settings__meta-value">
              {cfg.lastSyncAt
                ? dayjsTZ(cfg.lastSyncAt).format("DD/MM/YYYY HH:mm")
                : "—"}
            </span>
          </div>
        </div>

        {(cfg.lastSyncQuery ||
          cfg.backfillPageToken ||
          cfg.lastError ||
          lastSyncResult) && (
          <div className="gmail-inbox-settings__stack">
            {cfg.backfillPageToken ? (
              <Alert
                type="info"
                showIcon
                message="Ka faqe të tjera backfill — kliko Sync tani përsëri."
              />
            ) : null}
            {cfg.lastSyncQuery ? (
              <Alert
                type="info"
                showIcon
                message={`Query i fundit: ${cfg.lastSyncQuery}`}
              />
            ) : null}
            {cfg.lastError ? (
              <Alert
                type="error"
                showIcon
                message={`Gabimi i fundit: ${cfg.lastError}`}
              />
            ) : null}
            {lastSyncResult ? (
              <Alert
                type="success"
                showIcon
                message={`Rezultati sync: ${lastSyncResult.phase}`}
                description={`Fetched ${lastSyncResult.fetched}, queued ${
                  lastSyncResult.queued
                }, skipped ${lastSyncResult.skippedExisting}, AI ${
                  lastSyncResult.analyzed
                }${
                  lastSyncResult.hasMoreBackfill
                    ? " — ka më shumë backfill"
                    : ""
                }`}
              />
            ) : null}
          </div>
        )}
      </Section>

      <Section title="Udhëzues: Google Console + OpenAI">
        <Collapse
          defaultActiveKey={["wizard"]}
          items={[
            {
              key: "wizard",
              label: "Hap udhëzuesin (kopjo / ngjit)",
              children: (
                <GmailSetupWizard
                  saJson={saJson}
                  onSaJsonChange={setSaJson}
                  delegatedMailbox={cfg.delegatedMailbox || ""}
                  onDelegatedMailboxChange={(delegatedMailbox) =>
                    setCfg({ ...cfg, delegatedMailbox })
                  }
                  openaiKey={openaiKey}
                  onOpenaiKeyChange={setOpenaiKey}
                  hasServiceAccountJson={!!cfg.hasServiceAccountJson}
                  hasOpenAiKey={!!cfg.hasOpenAiKey}
                />
              ),
            },
          ]}
        />
      </Section>

      <Section title="Konfigurimi">
        <div className="gmail-inbox-settings__grid">
          <div className="gmail-inbox-settings__field">
            <label className="gmail-inbox-settings__label">
              Mënyra e autentikimit
            </label>
            <Select
              style={{ width: "100%" }}
              value={cfg.mode || "serviceAccount"}
              onChange={(mode) => setCfg({ ...cfg, mode })}
              options={[
                {
                  value: "serviceAccount",
                  label: "Service account (rekomanduar)",
                },
                {
                  value: "oauthRefresh",
                  label: "OAuth refresh token (fallback)",
                },
              ]}
            />
          </div>

          <div className="gmail-inbox-settings__field">
            <label className="gmail-inbox-settings__label">
              Mailbox e deleguar
            </label>
            <Input
              placeholder="hr@flex.al"
              value={cfg.delegatedMailbox || ""}
              onChange={(e) =>
                setCfg({ ...cfg, delegatedMailbox: e.target.value })
              }
            />
          </div>

          <div className="gmail-inbox-settings__field gmail-inbox-settings__field--full">
            <label className="gmail-inbox-settings__label">
              Query / label Gmail
            </label>
            <Input
              value={cfg.query || ""}
              onChange={(e) => setCfg({ ...cfg, query: e.target.value })}
            />
            <p className="gmail-inbox-settings__hint">
              Sync shton automatikisht <code>after:</code> (lookback, pastaj nga
              data e fundit — pa ripërsëritur email-et e njohura).
            </p>
          </div>

          <div className="gmail-inbox-settings__field">
            <label className="gmail-inbox-settings__label">
              Lookback fillestar (muaj)
            </label>
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              max={24}
              value={cfg.initialLookbackMonths || 6}
              onChange={(initialLookbackMonths) =>
                setCfg({ ...cfg, initialLookbackMonths })
              }
            />
            <p className="gmail-inbox-settings__hint">
              Sync i parë merr këta muaj; pastaj vetëm ditët e reja.
            </p>
          </div>

          <div className="gmail-inbox-settings__field">
            <label className="gmail-inbox-settings__label">
              Intervali i poll (min)
            </label>
            <InputNumber
              style={{ width: "100%" }}
              min={5}
              max={1440}
              value={cfg.pollMinutes || 15}
              onChange={(pollMinutes) => setCfg({ ...cfg, pollMinutes })}
            />
            <p className="gmail-inbox-settings__hint">
              Cron në prod ekzekutohet çdo 15 minuta.
            </p>
          </div>
        </div>
      </Section>

      <Section title="OpenAI & sekertet">
        <div className="gmail-inbox-settings__grid">
          <div className="gmail-inbox-settings__field gmail-inbox-settings__field--full">
            <div className="gmail-inbox-settings__label-row">
              <label className="gmail-inbox-settings__label">
                OpenAI model
              </label>
              <MondayButton
                className="mondayButtonBlue"
                icon={<ReloadOutlined />}
                onClick={loadModels}
                disabled={loadingModels}
              >
                {loadingModels ? "…" : "Rifresko modelet"}
              </MondayButton>
            </div>
            <Select
              style={{ width: "100%" }}
              showSearch
              loading={loadingModels}
              value={cfg.openaiModel || "gpt-4o-mini"}
              onChange={(openaiModel) => setCfg({ ...cfg, openaiModel })}
              options={modelSelectOptions}
              placeholder="Zgjidh modelin"
            />
            <p className="gmail-inbox-settings__hint">
              Lista nga OpenAI API
              {modelsSource === "fallback" ? " (fallback lokal)" : ""}. Ruaj për
              ta përdorur në sync.
            </p>
          </div>

          {canEditSecrets ? (
            <>
              <div className="gmail-inbox-settings__field gmail-inbox-settings__field--full">
                <label className="gmail-inbox-settings__label">
                  OpenAI API key{" "}
                  {cfg.hasOpenAiKey ? (
                    <span className="gmail-inbox-settings__ok">
                      (i konfiguruar)
                    </span>
                  ) : (
                    <span className="gmail-inbox-settings__warn">(mungon)</span>
                  )}
                </label>
                <Input.Password
                  placeholder="sk-… (lije bosh për ta mbajtur ekzistuesen)"
                  value={openaiKey}
                  onChange={(e) => setOpenaiKey(e.target.value)}
                  autoComplete="new-password"
                />
                <p className="gmail-inbox-settings__hint">
                  Shkon te SSM <code>/flexhr/&lt;stage&gt;/openai/apiKey</code>.
                </p>
              </div>

              <div className="gmail-inbox-settings__field gmail-inbox-settings__field--full">
                <label className="gmail-inbox-settings__label">
                  Service account JSON{" "}
                  {cfg.hasServiceAccountJson ? (
                    <span className="gmail-inbox-settings__ok">
                      (i konfiguruar)
                    </span>
                  ) : (
                    <span className="gmail-inbox-settings__warn">(mungon)</span>
                  )}
                </label>
                <Input.TextArea
                  rows={5}
                  placeholder="Ngjit JSON e re për ta zëvendësuar (lije bosh për ta mbajtur ekzistuesen)"
                  value={saJson}
                  onChange={(e) => setSaJson(e.target.value)}
                />
              </div>

              {cfg.mode === "oauthRefresh" ? (
                <div className="gmail-inbox-settings__field gmail-inbox-settings__field--full">
                  <label className="gmail-inbox-settings__label">
                    OAuth refresh token{" "}
                    {cfg.hasOAuthRefresh ? (
                      <span className="gmail-inbox-settings__ok">
                        (i konfiguruar)
                      </span>
                    ) : (
                      <span className="gmail-inbox-settings__warn">
                        (mungon)
                      </span>
                    )}
                  </label>
                  <Input.Password
                    value={oauthToken}
                    onChange={(e) => setOauthToken(e.target.value)}
                    placeholder="Vendos token të ri për ta zëvendësuar"
                  />
                </div>
              ) : null}
            </>
          ) : (
            <p className="gmail-inbox-settings__hint">
              Sekretet e Google/OpenAI menaxhohen nga Admin i kompanisë dhe
              trashëgohen automatikisht për këtë degë.
            </p>
          )}
        </div>

        {testResult ? (
          <div className="gmail-inbox-settings__test-result" role="status">
            <div className="gmail-inbox-settings__test-result-head">
              <span className="gmail-inbox-settings__test-result-dot" />
              <div>
                <p className="gmail-inbox-settings__test-result-label">
                  Lidhja u verifikua
                </p>
                <p className="gmail-inbox-settings__test-result-email">
                  {testResult.emailAddress}
                </p>
              </div>
            </div>
            <div className="gmail-inbox-settings__test-result-labels">
              <span className="gmail-inbox-settings__test-result-labels-title">
                Labels
              </span>
              <div className="gmail-inbox-settings__chips">
                {(testResult.labels || []).slice(0, 12).map((name) => (
                  <span key={name} className="gmail-inbox-settings__chip">
                    {name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </Section>

      <div className="gmail-inbox-settings__footer">
        <MondayButton
          className="mondayButtonGreen"
          onClick={save}
          disabled={saving}
        >
          {saving ? "Duke ruajtur…" : "Ruaj"}
        </MondayButton>
        <MondayButton
          className="mondayButtonBlue"
          onClick={test}
          disabled={testing}
        >
          {testing ? "Duke testuar…" : "Testo lidhjen"}
        </MondayButton>
        <MondayButton
          className="mondayButtonYellow"
          onClick={syncNow}
          disabled={syncing}
        >
          {syncing ? "Sync…" : "Sync tani"}
        </MondayButton>
        <MondayButton
          className="mondayButtonRed"
          onClick={resetBackfill}
          disabled={saving}
        >
          Rivendos backfill
        </MondayButton>
      </div>
    </div>
  );
};

export default GmailInboxSettings;
